import { GrowthBook } from '@growthbook/growthbook-react';
import { LocalStorageStickyBucketService } from '@growthbook/growthbook';

export const growthbook = new GrowthBook({
  apiHost: 'https://cdn.growthbook.io',
  clientKey: import.meta.env.VITE_GROWTHBOOK_KEY,
  enableDevMode: import.meta.env.VITE_TEMP_MEE_ENV === 'development',
  subscribeToChanges: true,
  trackingCallback: (experiment, result) => {
    console.log('Experiment Viewed', {
      experimentId: experiment.key,
      variationId: result.key,
    });
  },
  stickyBucketService: new LocalStorageStickyBucketService(),
});

// Expose growthbook to window for debugging in development
if (
  import.meta.env.DEV ||
  import.meta.env.VITE_TEMP_MEE_ENV === 'development'
) {
  window._growthbook = growthbook;

  // Helper function to clear GrowthBook cache and refresh features
  window.clearGrowthBookCache = async () => {
    // Clear localStorage items related to GrowthBook
    const keys = Object.keys(localStorage);
    keys.forEach((key) => {
      if (key.includes('growthbook') || key.includes('gb_')) {
        localStorage.removeItem(key);
      }
    });

    // Refresh features from the server
    await growthbook.refreshFeatures();

    // Reload the page to apply changes
    window.location.reload();
  };
}

export const showCandidateOvertime = () => growthbook.isOn('should-use-overtime');
export const getShouldShowFrontDeskSpecialty = () =>
  growthbook.isOn('should-show-frontdesk-specialty');
export const getShouldOfficeShowEfda = () => growthbook.isOn('should-office-show-efda');
export const getShouldAllowOfficeToPostProcedure = () =>
  growthbook.isOn('should-allow-office-to-post-procedure');
export const getShouldUsingCredentialServiceEndpoint = () =>
  growthbook.isOn('should-use-credential-service-endpoint');
export const getShouldFilterRenouncedProcedures = () =>
  growthbook.isOn('should-filter-renounced-procedures');
export const shouldDisplayPopupNjEmployeeLaunch = () =>
  growthbook.isOn('display-popup-nj-employee-launch');
export const enableNotificationReminder = () => growthbook.isOn('enable-notification-reminder');
export const enableChatsUnread = () => growthbook.isOn('enable-chats-unread');
export const getShouldEnableUnlimitedCandidatesList = () =>
  growthbook.isOn('enable-unlimited-candidates-list');

export const getShouldEnableCandidatesListSort = () =>
  growthbook.isOn('enable-candidates-list-sort');

export const enableLastJobPostedReminders = () =>
  growthbook.isOn('enable-last-job-posted-reminders');
export const enablePaymentExpirationReminders = () =>
  growthbook.isOn('enable-payment-expiration-reminders');
export const enableProfileCompletenessReminders = () =>
  growthbook.isOn('enable-profile-completeness-reminders');
export const getHideNotificationsOnParentOffice = () =>
  growthbook.isOn('hide-notifications-on-parent-office');

export const getShouldEnableNCNSRepostStates = () =>
  growthbook.getFeatureValue('enable-ncns-repost-states')?.split(',') || [];

export const shouldShowPrimaryOfficeContactPopup = () =>
  growthbook.isOn('show-primary-office-contact-popup');

export const shouldUseDsoStructure = () => growthbook.isOn('use-office-service');

export const shouldShowConfirmCandidateInBulk = () =>
  growthbook.isOn('show-confirm-candidate-in-bulk');

export const shouldShowDownloadReceiptButton = () =>
  growthbook.isOn('should-show-download-receipt-button');

export const getShouldEnableReferralProgramV2 = () =>
  growthbook.isOn('TEMP-10881-enable-referral-program-v2');

export const getShouldDisableEmployeeReferralStates = () =>
  growthbook.getFeatureValue('TEMP-12342-employee-referral-disabled-states', '')?.split(',') || [];

export const getShouldAllowOfficeToPostProcedureBasedOnProfession = () =>
  growthbook.isOn('TEMP-11985-should-allow-office-to-post-procedure-based-on-profession');

export const getShouldHideRequestReportButton = () =>
  growthbook.isOn('TEMP-13338-hide-request-report-button');

export const getShouldUsePendingReviews = () => growthbook.isOn('TEMP-13179-pending-reviews');

export const getShouldDomainDecommissioning = () =>
  growthbook.isOn('TEMP-13485-domain-decommissioning');

/**
 * return as string
 * e.g : +18558794688
 */
export const getShouldUseCSPhone = () => growthbook.getFeatureValue('TEMP-13494-CS-phone');

export const getShouldUseModernComponent = () =>
  growthbook.getFeatureValue('TEMP-13565-use-modern-component', {});

export const isTurnstileLoginEnabled = () => growthbook.isOn('turnstile-login-enabled');

export const getShouldShowRecommendedPros = () =>
  growthbook.isOn('TEMP-11357-recommended-pros-enable-fe');

export const getShouldExpandProfessionsJobPosting = () =>
  growthbook.isOn('TEMP-13688-expand-professions-job-posting');

export const getShouldUseParentPostShiftForChild = () =>
  growthbook.isOn('TEMP-13388-parent-post-shift-for-child');

export const getShouldUseOfficePolicyValidation = () =>
  growthbook.isOn('TEMP-13888-office-policy-validation');

export const getShouldUseSidebarRequestProfessionalMenu = () =>
  growthbook.getFeatureValue('TEMP-13847-sidebar-request-professional-menu', '')?.split(',') || [];

export const getShouldEnforceBestInDental = () => growthbook.isOn('ARF-163-enforce-best-in-dental');

export const getShouldEnforceProfessionalBestInDental = () =>
  growthbook.isOn('ARF-246-enforce-best-in-dental-professional');

export const getShouldUseAttestaion = () =>
  growthbook.isOn('TEMP-13710-should-use-attestiontion-fe');

export const getShouldUseNewOnboardingPopup = () =>
  growthbook.isOn('TEMP-14039-office-add-onboarding-tutorial');

export const shouldUseReferralProgramV2 = () => growthbook.isOn('should-use-referral-program-v2');

export const getShouldEnforcePermanentJobV2 = () =>
  growthbook.isOn('ARF-626-enforce-permanent-job-v2-dental-portal');

export const getShouldUseReportIncident = () =>
  growthbook.isOn(
    'TEMP-14277-dashboard-today-shift-page-add-invite-update-report-incident-dental-portal-mobile',
  );

export const getShouldShowBackgroundCheckBadge = () =>
  growthbook.isOn('TEMP-14730-show-background-check-badge');

export const getShouldShowChargeTimelineDaysPopup = () =>
  growthbook.isOn('TEMP-15208-charge-timeline-pop-up-for-second-pilot-dental-portal-mobile');

export const getShouldShowConsolidatedInvoices = () =>
  growthbook.isOn('TEMP-14775-enable-consolidated-invoices');

export const getShouldShowChargeTimelineBanner = () =>
  growthbook.isOn(
    'TEMP-15474-charge-timeline-add-banner-to-the-confirm-candidate-page-dental-portal-mobile',
  );

export const getShouldUseUpdatedChargeTimelinePopup = () =>
  growthbook.isOn('TEMP-15436-charge-timeline-update-pop-up-logic-copy-dental-portal-mobile');

export const getShouldUseInverseBulkConfirmInvites = () =>
  growthbook.isOn('TEMP-14606-Inverse-Bulk-Confirm-Invites');

export const getShouldUseSuspendedPostingsPopup = () =>
  growthbook.isOn('TEMP-15612-dental-portal-suspended-job-payment-pop-up-on-pm-selection');

export const getShouldShowRatingInviteToEligibleJobsScreen = () =>
  growthbook.isOn(
    'TEMP-15938-dental-prompt-the-office-to-invite-a-professional-to-more-shifts-if-they-rated-them-highly',
  );

export * from './growthbookValue';
